import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Mail, MessageCircle } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import { t, NAV_LINKS, LOGO } from "../i18n/translations";

const Footer = () => {
  const { lang, setLang } = useLang();
  const tr = t[lang];

  return (
    <footer data-testid="footer" className="bg-[#0A192F] text-white pt-20 pb-10">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Link to="/" className="inline-flex items-center gap-3" data-testid="footer-logo-link">
              <img src={LOGO.dark} alt="Revelo" className="h-12 w-12 object-contain" />
              <span className="font-bold text-2xl tracking-tight">Revelo</span>
            </Link>
            <p className="mt-5 text-slate-300 max-w-md leading-relaxed">
              {tr.footer.description}
            </p>
            <div className="mt-6 flex items-center gap-3">
              <div className="flex items-center rounded-full border border-white/15 overflow-hidden text-sm" data-testid="footer-language-switcher">
                <button
                  onClick={() => setLang("en")}
                  className={`px-3 py-1.5 ${lang === "en" ? "bg-[#E9A229] text-white" : "text-slate-300"}`}
                  data-testid="footer-lang-en"
                >EN</button>
                <button
                  onClick={() => setLang("zh")}
                  className={`px-3 py-1.5 ${lang === "zh" ? "bg-[#E9A229] text-white" : "text-slate-300"}`}
                  data-testid="footer-lang-zh"
                >中文</button>
              </div>
            </div>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-sm uppercase tracking-widest text-[#F2B53A] mb-4">{tr.footer.navTitle}</h4>
            <ul className="space-y-2.5">
              {NAV_LINKS.map((l) => (
                <li key={l.key}>
                  <Link to={l.path} className="text-slate-300 hover:text-white transition-colors" data-testid={`footer-link-${l.key}`}>
                    {tr.nav[l.key]}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="text-sm uppercase tracking-widest text-[#F2B53A] mb-4">{tr.footer.contactTitle}</h4>
            <ul className="space-y-3 text-slate-300">
              <li className="flex items-center gap-2.5">
                <Mail size={16} className="text-[#F2B53A]" />
                <a href="mailto:revelo.hk@gmail.com" className="hover:text-white" data-testid="footer-email">revelo.hk@gmail.com</a>
              </li>
              <li className="flex items-center gap-2.5">
                <MessageCircle size={16} className="text-[#F2B53A]" />
                <a href="https://wa.me/85254210539" className="hover:text-white" data-testid="footer-whatsapp">WhatsApp: 54210539</a>
              </li>
              <li className="flex items-center gap-2.5">
                <Instagram size={16} className="text-[#F2B53A]" />
                <a href="https://instagram.com/revelo.hk" target="_blank" rel="noreferrer" className="hover:text-white" data-testid="footer-instagram">@revelo.hk</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-slate-400">
          <p data-testid="footer-copyright">{tr.footer.copyright}</p>
          <p className="text-xs tracking-wider uppercase">Revealing truth, reviving trust.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
